/**
 * Cart drawer A/B discount code field.
 * Applies / clears codes via Cart AJAX API: POST /cart/update.js { discount }.
 */
class CartDrawerAbDiscount extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.formEl = this.querySelector('[data-cart-ab-discount-form]');
    this.fieldEl = this.querySelector('[data-cart-ab-discount-field]');
    this.inputEl = this.querySelector('[data-cart-ab-discount-input]');
    this.applyBtn = this.querySelector('[data-cart-ab-discount-apply]');
    this.errorEl = this.querySelector('[data-cart-ab-discount-error]');
    this.appliedEl = this.querySelector('[data-cart-ab-discount-applied]');
    this.codeEl = this.querySelector('[data-cart-ab-discount-code]');
    this.changeBtn = this.querySelector('[data-cart-ab-discount-change]');
    this.toggleBtn = this.querySelector('[data-cart-ab-discount-toggle]');
    this.panelEl = this.querySelector('[data-cart-ab-discount-panel]');

    this.applyBtn?.addEventListener('click', () => this.applyCode());
    this.changeBtn?.addEventListener('click', () => this.clearCode());
    this.toggleBtn?.addEventListener('click', () => this.togglePanel());
    this.inputEl?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.applyCode();
      }
    });
    this.inputEl?.addEventListener('input', () => this.clearError());

    this.syncFromCart();
  }

  /**
   * @returns {string}
   */
  get rootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  /**
   * @returns {string}
   */
  get updateUrl() {
    return window.routes?.cart_update_url || `${this.rootUrl}cart/update.js`;
  }

  togglePanel() {
    const collapsed = this.classList.toggle('is-collapsed');
    this.toggleBtn?.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
  }

  clearError() {
    this.fieldEl?.classList.remove('is-invalid');
    if (this.errorEl) this.errorEl.hidden = true;
  }

  /**
   * @param {string} code
   */
  showApplied(code) {
    if (this.formEl) this.formEl.hidden = true;
    if (this.appliedEl) this.appliedEl.hidden = false;
    if (this.codeEl) this.codeEl.textContent = code;
    this.classList.add('is-applied');
    this.classList.remove('is-collapsed');
    this.toggleBtn?.setAttribute('aria-expanded', 'true');
    this.clearError();
  }

  showForm(prefill = '') {
    if (this.formEl) this.formEl.hidden = false;
    if (this.appliedEl) this.appliedEl.hidden = true;
    if (this.inputEl) this.inputEl.value = prefill;
    if (this.codeEl) this.codeEl.textContent = '';
    this.classList.remove('is-applied');
  }

  /**
   * Only codes Shopify marks applicable count as applied.
   * Invalid attempts stay in cart.discount_codes with applicable: false.
   * @param {object} cart
   * @returns {string}
   */
  getApplicableCode(cart) {
    const codes = Array.isArray(cart?.discount_codes)
      ? cart.discount_codes
      : [];
    const match = codes.find(
      (entry) => entry?.code && entry.applicable === true,
    );
    return match?.code || '';
  }

  async syncFromCart() {
    try {
      const response = await fetch(`${this.rootUrl}cart.js`, {
        credentials: 'same-origin',
      });
      if (!response.ok) return;

      const cart = await response.json();
      const code = this.getApplicableCode(cart);

      if (code) {
        this.showApplied(code);
      } else {
        this.showForm();
      }
    } catch (error) {
      console.warn('[cart-drawer-ab-discount] sync failed', error);
    }
  }

  /**
   * @param {string} discount
   * @returns {Promise<object>}
   */
  async updateDiscount(discount) {
    const response = await fetch(this.updateUrl, {
      ...fetchConfig(),
      body: JSON.stringify({ discount }),
    });

    if (!response.ok) {
      throw new Error(`Discount update failed (${response.status})`);
    }

    return response.json();
  }

  async refreshCartSections() {
    const url = `${this.rootUrl}?sections=cart-drawer,cart-icon-bubble`;
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return;

    const sections = await response.json();
    const drawerHtml = sections['cart-drawer'];
    if (!drawerHtml) return;

    const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
    const nextInner = doc.querySelector('.drawer__inner');
    const currentInner = document.querySelector('#CartDrawer .drawer__inner');
    if (nextInner && currentInner) {
      currentInner.innerHTML = nextInner.innerHTML;
    }

    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleHtml = sections['cart-icon-bubble'];
    if (bubble && bubbleHtml) {
      const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const nextBubble = bubbleDoc.querySelector('.shopify-section');
      if (nextBubble) bubble.innerHTML = nextBubble.innerHTML;
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-ab-discount' });
    }
  }

  setLoading(isLoading) {
    if (this.applyBtn) this.applyBtn.disabled = isLoading;
    if (this.changeBtn) this.changeBtn.disabled = isLoading;
    if (this.inputEl) this.inputEl.disabled = isLoading;
  }

  async applyCode() {
    const code = (this.inputEl?.value || '').trim();
    if (!code) {
      this.fieldEl?.classList.add('is-invalid');
      if (this.errorEl) this.errorEl.hidden = false;
      return;
    }

    this.setLoading(true);
    this.clearError();

    try {
      const cart = await this.updateDiscount(code);
      const codes = Array.isArray(cart?.discount_codes)
        ? cart.discount_codes
        : [];
      const match = codes.find(
        (entry) =>
          entry?.code &&
          entry.code.toLowerCase() === code.toLowerCase(),
      );

      if (!match || match.applicable !== true) {
        // Shopify keeps invalid codes on the cart; clear so refresh
        // does not treat them as applied.
        try {
          await this.updateDiscount('');
        } catch (clearError) {
          console.warn(
            '[cart-drawer-ab-discount] clear invalid failed',
            clearError,
          );
        }

        this.showForm(code);
        this.fieldEl?.classList.add('is-invalid');
        if (this.errorEl) this.errorEl.hidden = false;
        return;
      }

      await this.refreshCartSections();
    } catch (error) {
      console.warn('[cart-drawer-ab-discount] apply failed', error);
      this.showForm(code);
      this.fieldEl?.classList.add('is-invalid');
      if (this.errorEl) this.errorEl.hidden = false;
    } finally {
      this.setLoading(false);
    }
  }

  async clearCode() {
    this.setLoading(true);

    try {
      await this.updateDiscount('');
      await this.refreshCartSections();
    } catch (error) {
      console.warn('[cart-drawer-ab-discount] clear failed', error);
      this.showForm();
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get('cart-drawer-ab-discount')) {
  customElements.define('cart-drawer-ab-discount', CartDrawerAbDiscount);
}
